'use client';

import { motion } from 'framer-motion';
import { Children, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { useDeviceCheck } from '@/shared/hooks';

interface FadeUpContainerProps extends PropsWithChildren {
  delay?: number;
  className?: string;
  itemClassName?: string;
  disable?: ('mobile' | 'tablet' | 'desktop')[];
}

const FadeUpContainer = ({
  children,
  delay = 0.1,
  className,
  itemClassName,
  disable,
}: FadeUpContainerProps) => {
  const { isMobile, isTablet, isDesktop } = useDeviceCheck();
  const disableDeviceSet = new Set(disable);

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: delay,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (
    (isMobile && disableDeviceSet.has('mobile')) ||
    (isTablet && disableDeviceSet.has('tablet')) ||
    (isDesktop && disableDeviceSet.has('desktop'))
  )
    return <>{children}</>;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className={className}
    >
      {Children.map(children, (child, index) => {
        if (!child) return null;

        return (
          <motion.div
            key={index}
            variants={childVariants}
            className={twMerge('inline-block w-full', itemClassName)}
          >
            {child}
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default FadeUpContainer;
